// Answer-shaped use-case pages: one data file, one renderer. Facts only —
// every claim maps to a shipped feature (EXIF fields from lib/exif.js).
export const EXIF_FIELDS = [
  "ImageDescription — session name + tags",
  "XPKeywords — tags (Windows / Lightroom keyword search)",
  "UserComment — full capture record",
  "Artist — session name",
  "DateTimeOriginal / DateTimeDigitized — capture time",
  "Software — AIImageSuite.pro / Session Capture",
];

export const USE_CASES = {
  "studio-batch-rename-exif": {
    audience: "PRODUCT PHOTOGRAPHERS & RESELLERS",
    title: "Batch-Rename and EXIF-Tag Product Photos at Capture — No Post-Shoot Renaming",
    h1: ["NAME 500 PRODUCT PHOTOS", "WHILE YOU SHOOT THEM."],
    intro: "A studio batch-rename and EXIF tool that runs in the camera, not after it. Set the SKU and tags once per session; every shutter press writes a sequenced filename and bakes the metadata into the JPEG before it ever hits disk.",
    pains: [
      "30-SKU drop × 3 angles = 90 files to open, inspect and rename after the shoot",
      "Renamers see pixels; size S and size M of the same tee are identical to them",
      "Hero / back / detail order gets re-sorted by eye from thumbnails",
      "Filenames don't survive the next export; EXIF does",
    ],
    steps: [
      ["Create a session", "SKU prefix, base tags (brand, colour, size), starting sequence."],
      ["Shoot", "Each tap → SKU-0001.jpg, SKU-0002.jpg… tags and session baked into EXIF."],
      ["Next variant", "Duplicate the session, change one tag, keep shooting."],
      ["Export or sync", "ZIP export with names intact, or 1-tap Cloud Sync to AIImageSuite.pro."],
    ],
    faqs: [
      ["Does it rename files already on my disk?", "No. AIImageSuites names photos at capture. For an existing back catalogue use a post-hoc renamer; for every shoot from today onward, name at capture and skip the renaming job entirely."],
      ["Which EXIF fields are written?", EXIF_FIELDS.join("; ") + "."],
      ["Does it work with my DSLR?", "Through the maker's webcam utility (EOS Webcam Utility, Imaging Edge Webcam…) or an HDMI→UVC capture card, yes. Raw USB tethering is not readable by any browser — see the camera help page."],
      ["What does it cost?", "Capturing, naming, tagging, EXIF baking and export are free with a Google sign-in. Cloud Sync delivery to AIImageSuite.pro is $12/month, cancel anytime."],
    ],
    related: [["/compare/capture-time-naming-vs-renaming", "VS. POST-HOC RENAMERS"], ["/help/camera", "CAMERA COMPATIBILITY"], ["/pricing", "PRICING"]],
  },
  "inspection-photos": {
    audience: "CONTRACTORS, ADJUSTERS & INSPECTORS",
    title: "Inspection Photo App: Timestamped, Tagged, EXIF-Baked Site Photos from Any Phone Browser",
    h1: ["EVERY SITE PHOTO", "LABELLED BEFORE YOU LEAVE."],
    intro: "A mobile inspection-photo workflow with nothing to install: open the site in Safari or Chrome, name the job, shoot. Each photo carries the job, room or claim tags, a sequence number and the original capture time inside the file — the record travels with the image, not a separate spreadsheet.",
    pains: [
      "200 photos named IMG_8831 that nobody can map back to a room a week later",
      "Timestamps lost when photos are shared through messaging apps",
      "Separate note-taking that drifts from the photo order",
      "App-store installs and logins on every subcontractor's phone",
    ],
    steps: [
      ["Name the job", "Session = claim number or address; tags = room, trade, pre/post."],
      ["Walk and shoot", "CLAIM-4471-0001.jpg… capture time and tags baked into EXIF. Drone frames? Import them into the same session with the flight log."],
      ["Dictate a caption", "Optional voice note per session — transcribed and stored with the batch (50/day)."],
      ["Hand off", "ZIP export for the report, or Cloud Sync for the office to pull from AIImageSuite.pro."],
    ],
    faqs: [
      ["Does it need an app install?", "No. It is an installable web app (PWA) — works in the phone browser, can be added to the home screen, and keeps capturing offline; uploads resume when signal returns."],
      ["Is the capture time tamper-evident?", "The original capture time is written to EXIF DateTimeOriginal at capture. Like any JPEG metadata it can be edited with tools, so treat it as a strong record, not a legal seal."],
      ["Does it record GPS?", "Phone captures: not yet — the session name and tags are the location record. Imported photos: yes. JPEGs from a drone or GPS-enabled camera keep their embedded GPS, and a DJI .SRT or Airdata/Litchi flight log fills GPS, altitude and bearing for frames without a fix. Each session exports a GeoJSON of photo positions."],
      ["Can a whole crew share one job?", "Each person signs in with Google and sees their own sessions; batches are then collected centrally through Cloud Sync."],
    ],
    related: [["/workflows/drone", "DRONE WORKFLOW"], ["/help/camera", "WILL MY PHONE WORK?"], ["/pricing", "PRICING"], ["/contact", "ASK A QUESTION"]],
  },
  "dataset-capture": {
    audience: "AI / ML & COMPUTER-VISION TEAMS",
    title: "Dataset Image Capture with Labels Baked into EXIF — Sequenced, Tagged, Export-Ready",
    h1: ["COLLECT LABELLED IMAGES,", "NOT A LABELLING BACKLOG."],
    intro: "Capture training images with the class label, split and session written into the filename and EXIF at the moment of capture. Sequenced names mean deterministic ordering; tags in XPKeywords and UserComment mean the label survives every copy, resize and re-export until it reaches your loader.",
    pains: [
      "Label after the fact → labelling backlog grows faster than the dataset",
      "Filenames get normalised by tooling; sidecar CSVs get lost",
      "Class imbalance is invisible until you count files by hand",
      "Multiple collectors, multiple phones, zero naming consistency",
    ],
    steps: [
      ["One session per class", "Name = class label; tags = split, environment, collector."],
      ["Capture in bursts", "CLASS-0001.jpg… tags baked into EXIF. Counter shows how many you have per class."],
      ["Switch class", "Duplicate the session, change the label, keep going — balance as you shoot."],
      ["Export", "ZIP per session, or Cloud Sync. Read labels back with exiftool / piexif in your pipeline."],
    ],
    faqs: [
      ["How do I read the labels back?", "exiftool -ImageDescription -XPKeywords -UserComment *.jpg, or piexif / Pillow in Python. The same text is also in the filename."],
      ["Is capture resolution controllable?", "Captures use the camera's native stream resolution offered by the browser; the camera diagnostic on the setup page shows what each device delivers before you start."],
      ["Does it support multiple collectors?", "Yes — each collector signs in and captures to their own sessions; the naming scheme is enforced by the session so files stay consistent across phones."],
      ["Can I capture offline in the field?", "Yes. The PWA captures offline and resumes uploads when connectivity returns."],
    ],
    related: [["/compare/capture-time-naming-vs-renaming", "WHY NAME AT CAPTURE"], ["/help/camera", "DEVICE COMPATIBILITY"], ["/pricing", "PRICING"]],
  },
};

export const USE_CASE_LINKS = [
  ...Object.entries(USE_CASES).map(([slug, c]) => ({ to: `/use-cases/${slug}`, label: c.audience })),
  { to: "/workflows/drone", label: "DRONE OPERATORS" },
];
